import { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { mergeObjects } from '../utils';
import {
	formSchemaAtom,
	submissionAtom,
	formDefaultAtom,
	submissionGuidAtom,
	correlationGuidAtom
} from '../recoil/form';

export const useFormControls = (settings, fhirObject, fhirEndpoint, fhirFormUrl, resolver) => {
	const [fhirClient, setFhirClient] = useState(null);
	const [formSchemaState, setFormSchemaState] = useRecoilState(formSchemaAtom);
	const [submissionState, setSubmissionState] = useRecoilState(submissionAtom);
	const [formDefaultState, setFormDefaultState] = useRecoilState(formDefaultAtom);
	const submissionGuidValue = useRecoilValue(submissionGuidAtom);
	const correlationGuidValue = useRecoilValue(correlationGuidAtom);

	useEffect(() => {
		if (fhirObject) 
			setFhirClient(fhirObject);
	}, [fhirObject]);

	const createApiRequestHeader = () => {
		var headers = {
			'accept': 'application/json',
			'content-type': 'application/json',
			'Ocp-Apim-Subscription-Key': settings.apiSubscriptionKey,
			'X-Submission-ID': submissionGuidValue,
			'X-Correlation-ID': correlationGuidValue
		};

		if (fhirClient?.client && fhirClient?.getAuthorizationHeader) {
			headers['Authorization'] = fhirClient.getAuthorizationHeader();
		}

		return headers;
	}

	const sendToApi = (url, data, additionalHeaders = null) => {
		const header = {
			method: 'post',
			headers: createApiRequestHeader(),
			body: JSON.stringify(data)
		}

		if (additionalHeaders) {
			for (const [key, value] of Object.entries(additionalHeaders)) {
				header.headers[key] = value || '';
			}
		}

		return fetch(url, header).then((response) => {
			return response;
		}).catch((error) => {
			console.log('[fetch] There was an error processing this request. ' + error);
		});
	}

	const createEformPayload = (submissionPayload) => {
		let payload = {};

		if (fhirClient?.client) {
			try {
				payload = recursiveCreateEformPayload(settings.fhirPathEformPayload);

				if (submissionPayload) {
					payload = mergeObjects(submissionPayload, payload);
				}
			} catch {
				console.log('There was an error creating submission payload.');
			}
		}

		return payload;
	}

	const recursiveCreateEformPayload = (obj) => {
		if (obj.constructor.name !== 'Array' && typeof obj === 'object') {
			const payload = {};

			for (const [key, value] of Object.entries(obj)) {
				let val = recursiveCreateEformPayload(value);
				if (val !== null) {
					payload[key] = val;
				}
			}

			return payload;
		} else {
			let data;

			try {
				data = resolver(obj);
			} catch {
				console.error(`Unable to load fhirPath resource referenced in fhirPathEformPayload: ${obj}`);
			}

			return data;
		}
	}

	const getFormSchema = () => {
		const headers = {
			method: 'get',
			headers: createApiRequestHeader()
		};

		fetch(fhirFormUrl, headers).then((response) => {
			return response.json();
		}).then((data) => {
			setFormSchemaState(data);
		});
	}

	const getDraft = (draftGuid) => {
		return fhirClient.client.request(`Bundle/${draftGuid}`).then((data) => {
			// convert QuestionnaireResponse object to formio
			return sendToApi(`${fhirEndpoint}/bundle/converttoformio`, data).then((response) => {
				if (response && response.ok) {
					return response.json();
				}
			});
		}).catch(() => {
			console.log(`[getDraft] There was an error retrieving the draft ${draftGuid}`);
		});
	}

	const saveDraft = (isDraftButton = false, isShortCodeRequest = false) => {
		let message = '';
		let requestResponse = '';
		let shortCode = '';
		const draftQueryString = isDraftButton ? '1' : '0';
		const shortCodeQueryString = isShortCodeRequest ? '&code=1' : '';

		const requestBody = {
			data: submissionState.data,
			metadata: submissionState.metadata,
			eforms: createEformPayload(submissionState.eforms)
		};

		// convert formio submission to QuestionnaireResponse
		return sendToApi(`${fhirEndpoint}/formio/converttobundle?id=${formSchemaState._id}&draft=${draftQueryString}${shortCodeQueryString}`, requestBody).then((bundleResponse) => {
			if (bundleResponse && bundleResponse.ok) {
				shortCode = bundleResponse.headers.get('X-eForms-SubmissionShortCode');

				return bundleResponse.json();
			} else {
				throw new Error('There was a problem connecting to the API.');
			}
		}).then((json) => {
			return fhirClient.client.create(json).then((fhirResponse) => {
				if (fhirResponse && fhirResponse.status === 'ok') {
					message = 'Draft saved';
					console.log(`[saveDraft] ${message}.`);
				} else {
					throw new Error('There was a problem communicating with the EMR.');
				}
			});
		}).catch((error) => {
			message = 'There was a problem saving the draft. Please contact an administrator.';
			console.log('[saveDraft] ' + error.message);
		}).then(() => {
			return {
				response: requestResponse,
				message: message,
				shortCode: shortCode
			};
		});
	}

	const getPdf = (suc = '') => {
		if (!suc) {
			sendToApi(`${fhirEndpoint}/formio/requestsuc`, '').then((response) => {
				if (response?.ok) {
					suc = response.headers.get('X-eForms-SUC');
					window.open(`${fhirEndpoint}/formiosubmission?id=${submissionGuidValue}&suc=${suc}`);
				} else {
					throw new Error('There was an error retrieving the security token.');
				}
			})
		} else {
			window.open(`${fhirEndpoint}/formiosubmission?id=${submissionGuidValue}&suc=${suc}`);
		}
	}

	const savePdf = (isDraft = 0) => {
		let message = '';
		let requestResponse = '';
		let suc = '';
		const queryString = isDraft === 1 ? '&pdf-only=1&isdraft=1' : '&pdf-only=1';
		const payload = {
			data: submissionState.data,
			metadata: submissionState.metadata,
			eforms: createEformPayload(submissionState.eforms)
		}

		return sendToApi(`${fhirEndpoint}/formio/requestsuc`, '').then((response) => {
			if (response) {
				requestResponse = response.headers.get('X-eForms-Message');

				if (response.ok) {
					suc = response.headers.get('X-eForms-SUC');
					return sendToApi(`${fhirFormUrl}${queryString}`, payload);
				}
			}

			throw new Error('There was an error retrieving the security token.');
		}).then((response) => {
			if (response) {
				requestResponse = response.headers.get('X-eForms-Message');

				if (response.ok) {
					console.log('[savePdf] PDF saved.');
					getPdf(suc);

					return response;
				}
			}

			throw new Error('There was an error generating the PDF.');
		}).catch((error) => {
			message = 'There was a problem saving the PDF. Please contact an administrator.';
			console.log('[savePdf] ' + error.message);
		}).then(() => {
			return {
				response: requestResponse,
				message: message
			};
		});
	}

	const submit = (submission) => {
		let message = '';
		let requestResponse = '';

		submission.eforms = createEformPayload(submission.eforms);

		return sendToApi(fhirFormUrl, submission).then((response) => {
			requestResponse = response.headers.get('X-eForms-Message');

			if (response && response.ok) {
				const suc = response.headers.get('X-eForms-SUC');

				return saveDraft(false).then(() => {
					if (settings.features.enableAutoDownloadPdf) {
						getPdf(suc);

						message = 'A PDF copy of this form has been downloaded. Please review your browser\'s download file.';
					}

					return true;
				});
			} else {
				return saveDraft(true).then(() => {
					message = 'There was a problem submitting your request.';
					setFormDefaultState(submission);

					return false;
				});
			}
		}).then((isSubmitted) => {
			return {
				response: requestResponse,
				message: message,
				isSubmitted: isSubmitted
			};
		});
	}

	return {
		getFormSchema: getFormSchema,
		saveDraft: saveDraft,
		getDraft: getDraft,
		savePdf: savePdf,
		getPdf: getPdf,
		submit: submit
	};
}
